// Penyusun ronde kuis Bahasa Indonesia.
// Soal BI berupa soal kelompok: beberapa soal untuk satu teks bacaan, ditampilkan berurutan
// supaya anak cukup membaca teksnya sekali (seperti TKA). Sumber soal:
//   - bank soal (R2, bisa diedit admin), porsinya diatur bankSoal.porsi di config.json;
//   - soal pengembangan (dibundel di aplikasi, lihat pengembangan/*.json) untuk sisanya.
import { shuffle } from '../../core/random.js';
import { siapkanSoalBank } from '../bersama.js';
import { BOBOT_BAWAAN_BI } from './topik.js';

export const BOBOT_BAWAAN = BOBOT_BAWAAN_BI;
export const MAKS_PER_BACAAN = 5;

const kunciTopik = q => q.gen || String(q.topic || '').replace(/^bank:/, '');

// Kelompokkan soal per teks bacaan, urutan soal di dalam teks dipertahankan.
export function kelompokkan(soal) {
  const peta = new Map();
  soal.forEach((q, i) => {
    const k = q.bacaan?.id ? 'b:' + q.bacaan.id : 's:' + q.id;
    if (!peta.has(k)) peta.set(k, { jenis: q.bacaan?.jenis || q.el, soal: [] });
    peta.get(k).soal.push({ q, i });
  });
  return [...peta.values()];
}

// Pilih k soal dari satu kelompok, berbobot topik (Efraimidis–Spirakis), lalu urutkan kembali.
function pilihBerbobot(daftar, k, w) {
  return daftar
    .map(x => ({ x, s: Math.random() ** (1 / Math.max(0.01, w(x.q))) }))
    .sort((a, b) => b.s - a.s).slice(0, k).map(y => y.x)
    .sort((a, b) => a.i - b.i);
}

// Acak kelompok, lalu selang-seling per jenis teks supaya teks informasi dan fiksi seimbang.
function acakSeimbang(kelompok) {
  const per = new Map();
  for (const g of shuffle(kelompok)) { if (!per.has(g.jenis)) per.set(g.jenis, []); per.get(g.jenis).push(g); }
  const antre = shuffle([...per.values()]), out = [];
  while (antre.some(a => a.length)) for (const a of antre) if (a.length) out.push(a.shift());
  return out;
}

// Ambil kelompok acak sampai jumlah soal mencapai target.
function ambil(kelompok, target, w) {
  const out = [];
  let sisa = target;
  for (const g of acakSeimbang(kelompok)) {
    if (sisa <= 0) break;
    const k = Math.min(g.soal.length, sisa, MAKS_PER_BACAAN);
    // Hindari teks bacaan yang hanya mendapat satu soal bila masih bisa dibagi lebih rata.
    if (k === 1 && g.soal.length > 1 && out.length && out.at(-1).soal.length > 2) { out.at(-1).soal.pop(); sisa++; }
    const kk = Math.min(g.soal.length, sisa, MAKS_PER_BACAAN);
    out.push({ jenis: g.jenis, soal: pilihBerbobot(g.soal, kk, w) });
    sisa -= kk;
  }
  return out;
}

/**
 * Susun satu ronde berisi `n` soal.
 * @param {number} n
 * @param {{bobot?: Record<string, number>, bank?: object[], porsiBank?: number, pengembangan?: object[]}} opsi
 *   bank: soal bank (dinormalisasi); pengembangan: soal bawaan aplikasi (dinormalisasi);
 *   porsiBank: bagian ronde (0–1) dari bank soal; bobot: bobot per topik (0 = topik dimatikan).
 */
export function susunRonde(n, { bobot = {}, bank = [], porsiBank = 0, pengembangan = [] } = {}) {
  const bw = { ...BOBOT_BAWAAN, ...bobot };
  const w = q => bw[kunciTopik(q)] ?? 1;
  const aktif = q => w(q) > 0;
  const gBank = kelompokkan(bank.filter(aktif)), gDev = kelompokkan(pengembangan.filter(aktif));
  const jml = gs => gs.reduce((a, g) => a + g.soal.length, 0);

  const nBank = Math.min(jml(gBank), Math.round(n * Math.min(1, Math.max(0, porsiBank))));
  const dariBank = ambil(gBank, nBank, w);
  let dariDev = ambil(gDev, n - jml(dariBank), w);
  // Soal pengembangan kurang: tambah dari bank soal yang belum terpakai.
  if (jml(dariBank) + jml(dariDev) < n) {
    const pakai = new Set(dariBank.flatMap(g => g.soal.map(x => x.q.id)));
    const sisaBank = kelompokkan(bank.filter(q => aktif(q) && !pakai.has(q.id)));
    dariDev = [...dariDev, ...ambil(sisaBank, n - jml(dariBank) - jml(dariDev), w)];
  }

  // Urutkan kelompok: acak, tetapi selang-seling teks informasi dan fiksi bila bisa.
  return acakSeimbang([...dariBank, ...dariDev]).flatMap(g => g.soal.map(x => siapkanSoalBank(x.q)));
}
